import { useState, useEffect } from "react";

// 7 Different Text Animation Styles for "DESTINY JUNIOR"
const textAnimations = [
  {
    name: "Wave",
    className: "animate-wave",
    style: `
      @keyframes wave {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
    `
  },
  {
    name: "Bounce",
    className: "animate-bounce-letter",
    style: `
      @keyframes bounce-letter {
        0%, 100% { transform: translateY(0) scale(1); }
        50% { transform: translateY(-15px) scale(1.1); }
      }
    `
  },
  {
    name: "Glow Pulse",
    className: "animate-glow",
    style: `
      @keyframes glow {
        0%, 100% { text-shadow: 0 0 5px currentColor, 0 0 10px currentColor; filter: brightness(1); }
        50% { text-shadow: 0 0 20px currentColor, 0 0 30px currentColor, 0 0 40px currentColor; filter: brightness(1.3); }
      }
    `
  },
  {
    name: "Rotate 3D",
    className: "animate-rotate3d",
    style: `
      @keyframes rotate3d {
        0% { transform: perspective(500px) rotateY(0deg); }
        100% { transform: perspective(500px) rotateY(360deg); }
      }
    `
  },
  {
    name: "Typewriter",
    className: "animate-typewriter",
    style: `
      @keyframes typewriter {
        from { width: 0; }
        to { width: 100%; }
      }
      @keyframes blink {
        50% { border-color: transparent; }
      }
    `
  },
  {
    name: "Rainbow",
    className: "animate-rainbow",
    style: `
      @keyframes rainbow {
        0% { color: #FF6B6B; }
        14% { color: #FFA500; }
        28% { color: #FFD93D; }
        42% { color: #6BCB77; }
        57% { color: #4D96FF; }
        71% { color: #9B59B6; }
        85% { color: #FF6B9D; }
        100% { color: #FF6B6B; }
      }
    `
  },
  {
    name: "Shake",
    className: "animate-shake",
    style: `
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
      }
    `
  }
];

// Logo Animation Component
export function AnimatedLogo({ src, alt, className = "", animationType = "bounce" }) {
  return (
    <>
      <style>{`
        @keyframes logo-bounce {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.05) rotate(-3deg); }
          50% { transform: scale(1.1) rotate(0deg); }
          75% { transform: scale(1.05) rotate(3deg); }
        }
        @keyframes logo-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.9; }
        }
        @keyframes logo-swing {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(10deg); }
          75% { transform: rotate(-10deg); }
        }
        @keyframes logo-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-8px) rotate(2deg); }
          50% { transform: translateY(-12px) rotate(0deg); }
          75% { transform: translateY(-8px) rotate(-2deg); }
        }
        @keyframes logo-heartbeat {
          0%, 100% { transform: scale(1); }
          14% { transform: scale(1.15); }
          28% { transform: scale(1); }
          42% { transform: scale(1.15); }
          70% { transform: scale(1); }
        }
        .logo-bounce { animation: logo-bounce 2s ease-in-out infinite; }
        .logo-pulse { animation: logo-pulse 2s ease-in-out infinite; }
        .logo-swing { animation: logo-swing 2s ease-in-out infinite; transform-origin: top center; }
        .logo-float { animation: logo-float 3s ease-in-out infinite; }
        .logo-heartbeat { animation: logo-heartbeat 1.5s ease-in-out infinite; }
      `}</style>
      <img 
        src={src} 
        alt={alt}
        className={`${className} logo-${animationType}`}
      />
    </>
  );
}

// Animated Text Component with Letter-by-Letter Animation
export function AnimatedText({ 
  text = "DESTINY JUNIOR", 
  animationIndex = 0,
  className = "",
  destinyColor = "#EF4444",
  juniorColor = "#EAB308"
}) {
  const animation = textAnimations[animationIndex % textAnimations.length];
  const words = text.split(" ");
  
  return (
    <>
      <style>{animation.style}</style>
      <style>{`
        .letter-animate {
          display: inline-block;
          animation-fill-mode: both;
        }
        .wave-text .letter-animate {
          animation: wave 1s ease-in-out infinite;
        }
        .bounce-text .letter-animate {
          animation: bounce-letter 0.8s ease-in-out infinite;
        }
        .glow-text {
          animation: glow 2s ease-in-out infinite;
        }
        .rotate3d-text {
          animation: rotate3d 4s linear infinite;
          display: inline-block;
        }
        .typewriter-text {
          overflow: hidden;
          white-space: nowrap;
          animation: typewriter 2s steps(14) forwards;
          border-right: 3px solid;
          animation: typewriter 2s steps(14) forwards, blink 0.7s step-end infinite;
        }
        .rainbow-text {
          animation: rainbow 3s linear infinite;
        }
        .shake-text:hover {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
      <div className={`${className} ${animation.name.toLowerCase().replace(' ', '-')}-text`}>
        {words.map((word, wordIndex) => (
          <span 
            key={wordIndex}
            style={{ 
              color: wordIndex === 0 ? destinyColor : juniorColor,
              marginRight: wordIndex < words.length - 1 ? '0.3em' : 0
            }}
          >
            {animation.name === "Wave" || animation.name === "Bounce" ? (
              word.split('').map((letter, letterIndex) => (
                <span 
                  key={letterIndex}
                  className="letter-animate"
                  style={{ 
                    animationDelay: `${(wordIndex * word.length + letterIndex) * 0.1}s`
                  }}
                >
                  {letter}
                </span>
              ))
            ) : (
              word
            )}
          </span>
        ))}
      </div>
    </>
  );
}

// Demo Component showing all 7 animations
export function AnimationDemo({ onSelect }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [logoAnim, setLogoAnim] = useState("bounce");
  
  const logoAnimations = ["bounce", "pulse", "swing", "float", "heartbeat"];

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-bold text-center mb-4">Pilih Animasi</h2>
      
      {/* Logo Animation Selection */}
      <div className="bg-white rounded-2xl p-4 shadow-lg">
        <h3 className="font-semibold mb-3 text-gray-700">Animasi Logo:</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {logoAnimations.map((anim) => (
            <button
              key={anim}
              onClick={() => setLogoAnim(anim)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                logoAnim === anim 
                  ? 'bg-pink-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {anim.charAt(0).toUpperCase() + anim.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex justify-center py-4">
          <AnimatedLogo 
            src="/logo-destiny.png" 
            alt="Logo" 
            className="w-32 h-32 object-contain"
            animationType={logoAnim}
          />
        </div>
      </div>

      {/* Text Animation Selection */}
      <div className="bg-white rounded-2xl p-4 shadow-lg">
        <h3 className="font-semibold mb-3 text-gray-700">Animasi Tulisan (7 Jenis):</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {textAnimations.map((anim, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                selectedIndex === index 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {index + 1}. {anim.name}
            </button>
          ))}
        </div>
        <div className="text-center py-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
          <AnimatedText 
            text="DESTINY JUNIOR"
            animationIndex={selectedIndex}
            className="text-3xl font-bold"
            style={{ fontFamily: '"QD Better Comic Sans", "Comic Sans MS", cursive' }}
          />
        </div>
      </div>

      {/* Apply Button */}
      {onSelect && (
        <button
          onClick={() => onSelect({ logoAnim, textAnim: selectedIndex })}
          className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-purple-600 transition-all"
        >
          Gunakan Animasi Ini
        </button>
      )}
    </div>
  );
}

// Pre-built animated logo header for login page
export function AnimatedLogoHeader({ 
  logoAnimationType = "float",
  textAnimationIndex = 0 
}) {
  return (
    <div className="text-center mb-6">
      <div className="inline-flex items-center justify-center mb-4">
        <AnimatedLogo 
          src="/logo-destiny.png" 
          alt="Destiny Junior Logo" 
          className="w-40 h-40 object-contain drop-shadow-lg"
          animationType={logoAnimationType}
        />
      </div>
      <AnimatedText 
        text="DESTINY JUNIOR"
        animationIndex={textAnimationIndex}
        className="text-3xl font-bold mb-1"
      />
      <p className="text-gray-600 text-sm">Pengurusan Log Pelajar & Perkembangan</p>
    </div>
  );
}

export default AnimatedText;
